import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { CompanyPort, Port } from "../../../../../../models/port";
import { Filters, Searches, Sorts, Sort } from "../../../../../../models/shipeezi.table";
import PortService from "../../../../../../services/port.service";
import { useAppState } from "../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";

export const useActions = () => {
  const { currentUser } = useAppState();

  const [ports, setPorts] = useState<Port[]>([]);
  const [port, setPort] = useState<Port>();
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["port.list", page, pageSize, filters, searches, sorting],
    () =>
      PortService.getAll({
        page,
        pageSize,
        include: JSON.stringify([
          {
            association: "companyPorts",
            required: false,
            where: { companyId: currentUser?.companyId },
          },
          {
            association: "country",
          },
        ]),
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setPorts(response.data);
      },
    }
  );

  const { refetch: getPortTotal } = useQuery(
    ["port.total", filters, searches],
    () =>
      PortService.count({
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: switchStatus, isLoading: isUpdating } = useMutation(
    ["port.update"],
    ({ companyPort }: { companyPort: CompanyPort }) => PortService.switchStatus(companyPort),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getPortTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    variables: {
      ports,
      port,
      hasErrors,
      page,
      pageSize,
      total,
      isFetching,
      isUpdating,
    },
    setPageSize,
    setPort,
    switchStatus,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  };
};
